import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Descriptions, Space, Spin, Switch, Tag } from 'antd';
import { useAppSelector } from 'app/config/store';
import { AUTHORITIES } from 'app/config/constants';

const apiUrl = 'api/controle-acces';

type AccessRight = {
  optionId: string;
  inscriptionId: string;
  libelle: string;
  optionActive: boolean;
  droitActif: boolean;
};

interface IEleveDroitsControleAccesProps {
  eleveId: string;
  anneeScolaireId: string;
}

const EleveDroitsControleAcces: React.FC<IEleveDroitsControleAccesProps> = ({ eleveId, anneeScolaireId }) => {
  const [rights, setRights] = useState<AccessRight[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingOptionId, setSavingOptionId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const authorities = useAppSelector(state => state.authentication.account.authorities);
  const canEdit = authorities?.some(authority => [AUTHORITIES.ADMIN, AUTHORITIES.DIRECTEUR].includes(authority));

  const loadRights = useCallback(async () => {
    if (!eleveId || !anneeScolaireId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get<AccessRight[]>(`${apiUrl}/eleves/${eleveId}/droits`, { params: { anneeScolaireId } });
      setRights(response.data);
    } catch (_) {
      setError('Les droits de contrôle d’accès ne peuvent pas être chargés. Réessayez dans quelques instants.');
    } finally {
      setLoading(false);
    }
  }, [eleveId, anneeScolaireId]);

  useEffect(() => {
    loadRights();
  }, [loadRights]);

  const updateRight = async (right: AccessRight, actif: boolean) => {
    setSavingOptionId(right.optionId);
    setError('');
    try {
      await axios.patch(`${apiUrl}/options/${right.optionId}/inscriptions/${right.inscriptionId}`, { actif });
      setRights(current => current.map(item => (item.optionId === right.optionId ? { ...item, droitActif: actif } : item)));
    } catch (_) {
      setError(`La modification du droit « ${right.libelle} » n’a pas été enregistrée. Le réglage précédent a été conservé.`);
    } finally {
      setSavingOptionId(null);
    }
  };

  return (
    <>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
      {!canEdit && (
        <Alert type="info" showIcon message="Consultation seule : seuls un administrateur ou un directeur peuvent modifier ces droits." />
      )}
      <Spin spinning={loading}>
        <Descriptions column={1}>
          {rights.map(right => (
            <Descriptions.Item key={right.optionId} label={right.libelle}>
              <Space>
                {right.optionActive ? (
                  <>
                    <Tag color={right.droitActif ? 'green' : 'red'}>{right.droitActif ? 'Activé' : 'Désactivé'}</Tag>
                    <Switch
                      size="small"
                      checked={right.droitActif}
                      disabled={!canEdit || savingOptionId === right.optionId}
                      loading={savingOptionId === right.optionId}
                      aria-label={`Contrôle d’accès ${right.libelle}`}
                      onChange={checked => updateRight(right, checked)}
                    />
                  </>
                ) : (
                  <Tag>Option désactivée pour l’école</Tag>
                )}
              </Space>
            </Descriptions.Item>
          ))}
          {!loading && rights.length === 0 && <Descriptions.Item label="Contrôle d’accès">Aucune option disponible</Descriptions.Item>}
        </Descriptions>
      </Spin>
    </>
  );
};

export default EleveDroitsControleAcces;
